"use client";

import { useMemo, useState } from "react";
import { Link } from "@/lib/i18n/navigation";

type SymptomEntry = {
  labelFr: string;
  labelAr: string;
  labelEn: string;
  aliases: string[];
  primary: string;
  primarySpeName: string;
  alternates: string[];
  alternateNames: string[];
};

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

/**
 * Pré-triage symptômes — questionnaire structuré, PAS un modèle de langage.
 * Choix délibéré (cf bilan stratégique 2026-07-18, item III·02) : un arbre
 * de correspondance déterministe sur des données médicales pré-validées
 * (SYMPTOMS) est prévisible et vérifiable. Un LLM en accès direct sur ce
 * flux serait un risque médico-légal — aucune recommandation ici n'est
 * générée, seulement recherchée dans une table fixe.
 */
export function SymptomTriageTool({
  symptoms,
  locale,
  labels,
}: {
  symptoms: SymptomEntry[];
  locale: string;
  labels: {
    placeholder: string;
    noResults: string;
    primaryLabel: string;
    alsoConsider: string;
    searchCta: string;
    startOver: string;
    disclaimer: string;
  };
}) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<SymptomEntry | null>(null);

  const results = useMemo(() => {
    const q = normalize(query.trim());
    if (q.length < 2) return [];
    return symptoms
      .filter((s) => {
        const label = normalize(locale === "ar" ? s.labelAr : locale === "en" ? s.labelEn : s.labelFr);
        if (label.includes(q)) return true;
        return s.aliases.some((a) => normalize(a).includes(q));
      })
      .slice(0, 8);
  }, [query, symptoms, locale]);

  function localizedLabel(s: SymptomEntry): string {
    return locale === "ar" ? s.labelAr : locale === "en" ? s.labelEn : s.labelFr;
  }

  if (selected) {
    const label = localizedLabel(selected);
    return (
      <div className="rounded-3xl border border-[var(--sahha-border-default)] bg-white p-6">
        <p className="text-[13px] text-[var(--sahha-text-500)]">{label}</p>
        <h2 className="mt-1 text-xl font-bold text-[var(--sahha-text-900)]">{labels.primaryLabel}</h2>
        <Link
          href={`/recherche?q=${encodeURIComponent(selected.primarySpeName)}` as never}
          className="mt-4 flex items-center gap-3 rounded-2xl border-2 border-[var(--sahha-primary-300)] bg-[var(--sahha-primary-50)] p-4 transition hover:border-[var(--sahha-primary-400)]"
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--sahha-primary-700)] text-lg text-white">
            🩺
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-bold text-[var(--sahha-text-900)]">{selected.primarySpeName}</p>
            <p className="text-[12.5px] text-[var(--sahha-primary-700)]">{labels.searchCta} →</p>
          </div>
        </Link>

        {selected.alternateNames.length > 0 && (
          <div className="mt-4">
            <p className="text-[12px] font-semibold uppercase tracking-wider text-[var(--sahha-text-500)]">
              {labels.alsoConsider}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {selected.alternateNames.map((name) => (
                <Link
                  key={name}
                  href={`/recherche?q=${encodeURIComponent(name)}` as never}
                  className="rounded-full border border-[var(--sahha-border-default)] px-3 py-1.5 text-[12.5px] font-medium text-[var(--sahha-text-700)] hover:border-[var(--sahha-primary-300)] hover:text-[var(--sahha-primary-700)]"
                >
                  {name}
                </Link>
              ))}
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => {
            setSelected(null);
            setQuery("");
          }}
          className="mt-5 text-[13px] font-semibold text-[var(--sahha-text-600)] hover:text-[var(--sahha-text-900)]"
        >
          ← {labels.startOver}
        </button>

        <p className="mt-5 border-t border-[var(--sahha-border-subtle)] pt-4 text-[11.5px] leading-relaxed text-[var(--sahha-text-500)]">
          {labels.disclaimer}
        </p>
      </div>
    );
  }

  return (
    <div>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={labels.placeholder}
        autoComplete="off"
        className="w-full rounded-2xl border border-[var(--sahha-border-default)] px-5 py-4 text-[16px] outline-none focus:border-[var(--sahha-primary-400)] focus:ring-2 focus:ring-[var(--sahha-primary-200)]"
      />

      {query.trim().length >= 2 && (
        <div className="mt-3 divide-y divide-[var(--sahha-border-subtle)] rounded-2xl border border-[var(--sahha-border-default)] bg-white">
          {results.length === 0 ? (
            <p className="p-4 text-[13.5px] text-[var(--sahha-text-500)]">{labels.noResults}</p>
          ) : (
            results.map((s) => (
              <button
                key={s.labelFr}
                type="button"
                onClick={() => setSelected(s)}
                className="flex w-full items-center justify-between gap-3 p-4 text-left transition hover:bg-[var(--sahha-surface-1)]"
              >
                <span className="text-[14.5px] font-medium text-[var(--sahha-text-900)]">{localizedLabel(s)}</span>
                <span className="text-[var(--sahha-text-400)]">→</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
